import { Routes } from 'config/routes';
import { DateRangeType } from 'modules/dashboard/types/phlebotomist-dashboard';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  IRecruiterFilters,
  recruiterFiltersFromUrl,
  recruiterFiltersToUrl,
} from '../utils/recruiterFilters';

export function useRecruiterFilters() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingRef = useRef(false);

  const [filters, setFilters] = useState<IRecruiterFilters>(() =>
    recruiterFiltersFromUrl(searchParams)
  );

  const locationIds = filters.locationIds.length
    ? filters.locationIds
    : undefined;

  const isDashboardRoute =
    location.pathname === Routes.DashboardsRecruiterDashboard;
  useEffect(() => {
    if (!isDashboardRoute || isSyncingRef.current) return;
    const newParams = recruiterFiltersToUrl(filters);
    const currentStr = searchParams.toString();
    const newStr = newParams.toString();
    if (currentStr !== newStr) {
      isSyncingRef.current = true;
      setSearchParams(newParams, { replace: true });
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  }, [filters, searchParams, setSearchParams, isDashboardRoute]);

  const handleDateRangeChange = useCallback(
    (
      dateRange: DateRangeType,
      customDateRange?: { start: Date; end: Date }
    ) => {
      setFilters((prev) => ({ ...prev, dateRange, customDateRange }));
    },
    []
  );

  const handleRegionChange = useCallback((regionIds: string[]) => {
    setFilters((prev) => ({
      ...prev,
      regionIds,
      officeIds: [],
      locationIds: [],
    }));
  }, []);

  const handleOfficeChange = useCallback((officeIds: string[]) => {
    setFilters((prev) => ({ ...prev, officeIds }));
  }, []);

  const handleLocationChange = useCallback((locationIds: string[]) => {
    setFilters((prev) => ({ ...prev, locationIds }));
  }, []);

  return {
    filters,
    setFilters,
    locationIds,
    searchParams,
    handleDateRangeChange,
    handleRegionChange,
    handleOfficeChange,
    handleLocationChange,
  };
}
